import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, Button, IconButton, Tooltip, Stack, 
  Typography, Divider, List, ListItem, ListItemAvatar, ListItemText, Avatar, useMediaQuery, useTheme
} from '@mui/material';
import { 
  ThumbUpOutlined, 
  ChatBubbleOutline, 
  ShareOutlined,
  Message as MessageIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { REACCIONES_MAP } from './ReaccionesConstants';
import SharePostModal from './SharePostModal';
import axios from 'axios';

const API_SOCIAL = "https://apiweb.hitpoly.com/ajax/apiSocialController.php";

const PostInteractionBar = ({ 
  post, 
  onReactionSelect, 
  onCommentClick, 
  onOpenReactionsModal,
  currentUserId,
  currentUserName,
  queryUsers,
  activeProfile
}) => {
  const [showReactions, setShowReactions] = useState(false);
  const hoverTimerRef = useRef(null);
  const reactionPickerRef = useRef(null);

  const [reactionsUsers, setReactionsUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [fetched, setFetched] = useState(false);

  const [shareModalOpen, setShareModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(e.target)) {
        setShowReactions(false);
      }
    };
    if (showReactions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showReactions]);

  const handleMouseEnter = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setShowReactions(true), 500);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setShowReactions(false), 300);
  };

  const userReaction = REACCIONES_MAP[post.user_reaction_type];

  const reactionCounts = post.reactions_count || {};
  const topReactions = Object.entries(reactionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(entry => entry[0]);

  const totalLikes = parseInt(post.total_likes) || 0;

  const handleOpenMessage = () => {
    window.postMessage({
      type: 'OPEN_FLOATING_CHAT',
      partner: {
        id: post.user_id,
        name: post.user_full_name || `${post.nombre_real || ''} ${post.apellido_real || ''}`.trim() || 'Usuario',
        avatar: post.user_avatar,
        type: 'main'
      }
    }, '*');
  };

  if (isMobile) {
    return (
      <Box>
        <SharePostModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          post={post}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
          queryUsers={queryUsers}
          activeProfile={activeProfile}
        />

        <Box sx={{ px: 2, py: 0.8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ position: 'relative' }} ref={reactionPickerRef}>
              <Button
                size="small"
                onMouseEnter={handleMouseEnter}
                onClick={() => onReactionSelect(post.user_reaction_type ? post.user_reaction_type : 'like')}
                startIcon={userReaction ? <Box component="img" src={userReaction.img} sx={{ width: 18, height: 18 }} /> : <ThumbUpOutlined sx={{ fontSize: 18 }} />}
                sx={{ color: userReaction ? userReaction.color : '#65676b', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', minWidth: 0, px: 1, borderRadius: 1, '&:hover': { bgcolor: userReaction ? userReaction.bg : 'rgba(0,0,0,0.05)' } }}
              >
                {''}
              </Button>

              <AnimatePresence>
                {showReactions && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.8 }}
                    animate={{ opacity: 1, y: -50, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.8 }}
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: 0,
                      backgroundColor: 'white',
                      borderRadius: '30px',
                      padding: '4px 8px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                      display: 'flex',
                      gap: '4px',
                      zIndex: 1301,
                      border: '1px solid #eee'
                    }}
                    onMouseEnter={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); }}
                  >
                    {Object.entries(REACCIONES_MAP).map(([key, value]) => (
                      <Tooltip key={key} title={value.label} placement="top" arrow>
                        <motion.div whileHover={{ scale: 1.4, y: -10 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
                          <IconButton onClick={() => { onReactionSelect(key); setShowReactions(false); }} sx={{ p: 0.4 }}>
                            <Box component="img" src={value.img} sx={{ width: 28, height: 28 }} />
                          </IconButton>
                        </motion.div>
                      </Tooltip>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

            <IconButton size="small" onClick={onCommentClick} sx={{ color: '#65676b' }}>
              <ChatBubbleOutline sx={{ fontSize: 18 }} />
            </IconButton>

            <IconButton size="small" onClick={() => setShareModalOpen(true)} sx={{ color: '#65676b' }}>
              <ShareOutlined sx={{ fontSize: 18 }} />
            </IconButton>

            <IconButton size="small" onClick={handleOpenMessage} sx={{ color: '#65676b' }}>
              <MessageIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          {totalLikes > 0 && (
            <Tooltip
              title={
                <Box sx={{ p: 0.5, maxWidth: 220 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, px: 1, py: 0.5, display: 'block', borderBottom: '1px solid #eee', mb: 0.5, color: '#1c1e21' }}>
                    Reacciones
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {loadingUsers ? (
                      <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999' }}>Cargando...</Typography>
                    ) : reactionsUsers.length === 0 ? (
                      <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999' }}>Sin reacciones</Typography>
                    ) : (
                      reactionsUsers.map((reac, idx) => (
                        <ListItem key={idx} sx={{ px: 1, py: 0.4 }}>
                          <ListItemAvatar sx={{ minWidth: 32 }}>
                            <Avatar src={reac.avatar} sx={{ width: 24, height: 24 }} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${reac.nombre} ${reac.apellido}`}
                            primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 500, color: '#1c1e21' }}
                          />
                          {reac.reaction_type && REACCIONES_MAP[reac.reaction_type] && (
                            <Box component="img" src={REACCIONES_MAP[reac.reaction_type].img} sx={{ width: 16, height: 16, ml: 'auto', flexShrink: 0 }} />
                          )}
                        </ListItem>
                      ))
                    )}
                  </List>
                </Box>
              }
              placement="top"
              arrow
              onOpen={() => {
                if (!fetched) {
                  setFetched(true);
                  setLoadingUsers(true);
                  const url = `${API_SOCIAL}?action=postReactions&postId=${post.id}`;
                  axios.get(url).then(res => {
                    setReactionsUsers(res.data && Array.isArray(res.data) ? res.data : (post.reactions_detail || []));
                  }).catch(() => {
                    setReactionsUsers(post.reactions_detail || []);
                  }).finally(() => setLoadingUsers(false));
                }
              }}
            >
              <Stack direction="row" spacing={0.3} alignItems="center" sx={{ cursor: 'pointer' }}>
                <Stack direction="row" spacing={-1}>
                  {topReactions.map((type, i) => (
                    <Box key={type} sx={{
                      bgcolor: 'white',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: topReactions.length - i,
                      position: 'relative',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      border: '1.5px solid #fff'
                    }}>
                      <Box component="img" src={REACCIONES_MAP[type]?.img} sx={{ width: 11, height: 11 }} />
                    </Box>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                  {totalLikes}
                </Typography>
              </Stack>
            </Tooltip>
          )}
        </Box>
        <Divider sx={{ mx: 2, opacity: 0.6 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', position: 'relative' }} onMouseLeave={handleMouseLeave}>
      <SharePostModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        post={post}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        queryUsers={queryUsers}
        activeProfile={activeProfile}
      />
      
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack 
          direction="row" 
          spacing={0.5} 
          alignItems="center" 
          sx={{ cursor: 'pointer' }} 
          onClick={() => onOpenReactionsModal(post.id)}
        >
          <Stack direction="row" spacing={-1}>
            {topReactions.map((type, i) => (
              <Box key={type} sx={{ 
                bgcolor: 'white',
                borderRadius: '50%', 
                width: 22, 
                height: 22, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                zIndex: topReactions.length - i,
                position: 'relative',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                border: '1.5px solid #fff'
              }}>
                <Box component="img" src={REACCIONES_MAP[type]?.img} sx={{ width: 14, height: 14 }} />
              </Box>
            ))}
          </Stack>
          
          <Typography variant="body2" color="text.secondary" sx={{ '&:hover': { textDecoration: 'underline' }, ml: 0.5, fontSize: '0.85rem', fontWeight: 500 }}>
            {totalLikes > 0 ? (
              post.user_reaction_type 
                ? (totalLikes === 1 ? "Tú" : `Tú y ${totalLikes - 1} más`)
                : `${totalLikes}`
            ) : ""}
          </Typography>
        </Stack>

        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, fontSize: '0.85rem' }} 
          onClick={onCommentClick}
        >
          {post.total_comments} {post.total_comments === 1 ? 'comentario' : 'comentarios'}
        </Typography>
      </Box>

      <Divider sx={{ mx: 2, opacity: 0.6 }} />

      <Stack direction="row" sx={{ px: 1, py: 0.5, position: 'relative' }}>
        
        <Box sx={{ position: 'relative' }} ref={reactionPickerRef}>
          <AnimatePresence>
            {showReactions && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: -50, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                style={{
                  position: 'absolute',
                  left: 0,
                  backgroundColor: 'white',
                  borderRadius: '30px',
                  padding: '4px 8px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                  display: 'flex',
                  gap: '4px',
                  zIndex: 1301,
                  border: '1px solid #eee'
                }}
                onMouseEnter={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); }}
              >
                {Object.entries(REACCIONES_MAP).map(([key, value]) => (
                  <Tooltip key={key} title={value.label} placement="top" arrow>
                    <motion.div
                      whileHover={{ scale: 1.4, y: -10 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <IconButton 
                        onClick={() => { onReactionSelect(key); setShowReactions(false); }}
                        sx={{ p: 0.5 }}
                      >
                        <Box component="img" src={value.img} sx={{ width: 32, height: 32 }} />
                      </IconButton>
                    </motion.div>
                  </Tooltip>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <Button 
            fullWidth 
            onMouseEnter={handleMouseEnter}
            onClick={() => onReactionSelect(post.user_reaction_type ? post.user_reaction_type : 'like')} 
            startIcon={userReaction ? <Box component="img" src={userReaction.img} sx={{ width: 20, height: 20 }} /> : <ThumbUpOutlined />} 
            sx={{ 
                color: userReaction ? userReaction.color : '#65676b', 
                textTransform: 'none', 
                fontWeight: 600, 
                py: 1,
                borderRadius: 1,
                transition: 'all 0.2s',
                '&:hover': { bgcolor: userReaction ? userReaction.bg : 'rgba(0,0,0,0.05)' }
            }}
          >
            {userReaction ? userReaction.label : 'Me gusta'}
          </Button>
        </Box>

        <Button 
          sx={{ flex: 1, color: '#65676b', textTransform: 'none', fontWeight: 600, borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }} 
          startIcon={<ChatBubbleOutline />} 
          onClick={onCommentClick}
        >
          Comentar
        </Button>

        <Button 
          sx={{ flex: 1, color: '#65676b', textTransform: 'none', fontWeight: 600, borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }} 
          startIcon={<ShareOutlined />}
          onClick={() => setShareModalOpen(true)}
        >
          Compartir
        </Button>

        <Button 
          sx={{ flex: 1, color: '#65676b', textTransform: 'none', fontWeight: 600, borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }} 
          startIcon={<MessageIcon />}
          onClick={handleOpenMessage}
        >
          Mensaje
        </Button>
      </Stack>
    </Box>
  );
};

export default PostInteractionBar;
